'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Car, CheckCircle2 } from 'lucide-react';
import { RegistrationLookup } from '@/components/RegistrationLookup';
import { VinLookup } from '@/components/VinLookup';
import { useCartStore } from '@/stores/cart';

/**
 * "Hitta exakt rätt delar" — regnummer ELLER chassinummer (VIN) på sidor man når
 * genom att bläddra märke → modell → kategori. Ägaren 2026-10-02: den vägen gick
 * hela vägen till 2 629 kylsystemdelar "till Renault" utan att någonstans fråga
 * efter bilen — och den används ofta just när bilen har utländska skyltar, så VIN
 * måste gå att ange.
 *
 * Efter sökningen: samma kategori filtrerad på bilen (kategoriSlug), annars bilens
 * egen sida. Är en bil redan vald visas en direktlänk i stället för formuläret.
 */
export function HittaRattDelar({
  rubrik = 'Hitta exakt rätt delar till din bil',
  text = 'Ange registreringsnummer eller chassinummer (VIN) så visar vi bara delar som passar.',
  kategoriSlug,
  source = 'katalog',
}: {
  rubrik?: string;
  text?: string;
  kategoriSlug?: string;
  source?: string;
}) {
  const router = useRouter();
  const vald = useCartStore((s) => s.selectedVehicle);
  const [monterad, setMonterad] = useState(false);
  const [flik, setFlik] = useState<'reg' | 'vin'>('reg');
  const [visaFormular, setVisaFormular] = useState(false);
  useEffect(() => setMonterad(true), []);

  const mal = (ktype: number) => (kategoriSlug ? `/katalog/${kategoriSlug}?ktype=${ktype}` : `/fordon/${ktype}`);
  const ga = () => {
    const k = useCartStore.getState().selectedVehicle?.ktype;
    if (k) router.push(mal(k));
  };

  if (monterad && vald?.ktype && !visaFormular) {
    const bil = `${vald.manufacturer} ${(vald.model ?? '').replace(/\s*\(.*$/, '')}`;
    return (
      <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-xl border border-brand/20 bg-cream/40 px-4 py-3 text-sm">
        <CheckCircle2 className="h-5 w-5 text-brand shrink-0" aria-hidden />
        <span className="flex-1 min-w-0">Din bil: <strong>{bil}</strong>{vald.cartype ? ` · ${vald.cartype}` : ''}</span>
        <button type="button" onClick={() => router.push(mal(vald.ktype!))} className="font-semibold text-brand hover:underline">
          Visa bara delar som passar →
        </button>
        <button type="button" onClick={() => setVisaFormular(true)} className="text-muted-foreground hover:text-foreground underline">
          Annan bil
        </button>
      </div>
    );
  }

  return (
    <section className="mb-6 rounded-xl border-2 border-brand/20 bg-cream/40 p-4 sm:p-5">
      <h2 className="flex items-center gap-2 font-heading text-base sm:text-lg text-brand">
        <Car className="h-5 w-5 shrink-0" aria-hidden /> {rubrik}
      </h2>
      <p className="text-sm text-muted-foreground mt-1 mb-3">{text}</p>
      <div className="max-w-xl">
        <div className="flex rounded-lg border overflow-hidden mb-3 bg-white">
          <button type="button" onClick={() => setFlik('reg')}
            className={`flex-1 px-2 py-2 text-sm font-semibold ${flik === 'reg' ? 'bg-brand text-white' : 'text-gray-600 hover:text-gray-900'}`}>
            Regnummer
          </button>
          <button type="button" onClick={() => setFlik('vin')}
            className={`flex-1 px-2 py-2 text-sm font-semibold ${flik === 'vin' ? 'bg-brand text-white' : 'text-gray-600 hover:text-gray-900'}`}>
            Chassinummer (VIN)
          </button>
        </div>
        {flik === 'reg'
          ? <RegistrationLookup compact stayOnPage source={source} onComplete={ga} onSwitchToVin={() => setFlik('vin')} />
          : <VinLookup stayOnPage onComplete={ga} />}
        <p className="text-xs text-muted-foreground mt-2">
          Utländsk bil? Chassinumret (17 tecken) står i registreringsbeviset och längst ner på vindrutan.
        </p>
      </div>
    </section>
  );
}
